import * as React from "react";
import { Button, Checkbox, DatePicker, Input, SearchableSelect, cn, useToast } from "@racoon/ui";
import { IcBin } from "@racoon/icons";
import { Namespace, useT, type TranslationKey } from "@racoon/i18n";
import { ConnectProto, ContractStatus } from "@racoon/api";
import {
  isDateRangeValid,
  isRejectedAmountKey,
  toNonNegativeInteger,
  toTimestamp,
} from "@racoon/lib";
import {
  useCreateContractMutation,
  useUpdateContractMutation,
  useUploadContractFilesMutation,
  useListContractsQuery,
} from "~/shared/api/contract/contract.grpc";
import { useJobTypeOptions } from "~/shared/hooks/useJobTypeOptions";
import { JobTypePicker } from "~/shared/ui/job-type-picker";
import { useContractFilePicker } from "~/features/customer-card/hooks/useContractFilePicker";
import { browserFileToProtoFile } from "~/features/customer-card/lib/fileUploadUtils";
import { OnboardingField, Section } from "./onboarding-layout";
import { DISABLED_BOX_CLASS, DISABLED_INPUT_CLASS } from "~/shared/ui/disabled-field";
import { ModeTabs } from "~/shared/ui/mode-tabs";
import { StepShell } from "./step-shell";
import {
  buildTariffRate,
  contractToFormValues,
  setJobTypeTariffAmount,
  toAmount,
  toggleJobTypeTariff,
  type JobTypeTariff,
} from "~/shared/lib/tariff-rate";

type ContractMode = "new" | "existing";

type Props = {
  providerId?: number;
  organizationId?: bigint;
  customerName?: string;
  contractId?: bigint;
  /** Whether the form holds input that was never saved — the wizard confirms closing over it. */
  onDirtyChange: (dirty: boolean) => void;
  onCreated: (contractId: bigint) => void;
  /** Picking one the customer already has, rather than drawing up a new one. */
  onPickedExisting: (contractId: bigint) => void;
};

const ONE_YEAR_MS = 365 * 24 * 60 * 60 * 1000;

/** Step 2 — the contract. The design's "recommended price" block has no RPC behind it and is left out. */
export const ContractStep: React.FC<Props> = ({
  providerId,
  organizationId,
  customerName,
  contractId,
  onDirtyChange,
  onCreated,
  onPickedExisting,
}) => {
  const t = useT();
  const { toast } = useToast();

  const [dealNumber, setDealNumber] = React.useState("");
  const [name, setName] = React.useState("");
  const [startDate, setStartDate] = React.useState<Date | undefined>(() => new Date());
  const [endDate, setEndDate] = React.useState<Date | undefined>(
    () => new Date(Date.now() + ONE_YEAR_MS),
  );
  const [tariffs, setTariffs] = React.useState<JobTypeTariff[]>([]);
  const [isRentBunker, setIsRentBunker] = React.useState(false);
  const [rentCost, setRentCost] = React.useState<number | "">(0);
  const [files, setFiles] = React.useState<File[]>([]);
  const [mode, setMode] = React.useState<ContractMode>("new");
  const [selectedExistingId, setSelectedExistingId] = React.useState("");
  // "Редактировать" unlocks the picked contract's fields for an update.
  const [isEditingExisting, setIsEditingExisting] = React.useState(false);
  // Only a contract created by this form may be updated from the "new" tab; a picked one
  // is edited on its own tab, after its fields have been loaded.
  const [createdId, setCreatedId] = React.useState<bigint>();

  const [createContract, { isLoading: creating }] = useCreateContractMutation();
  const [updateContract, { isLoading: updating }] = useUpdateContractMutation();
  const [uploadContractFiles, { isLoading: uploading }] = useUploadContractFilesMutation();

  const isExisting = mode === "existing";
  const isSaved = contractId != null;
  const createdHere = isSaved && contractId === createdId;
  const isBusy = creating || updating || uploading;
  const isDateRangeInvalid = !isDateRangeValid(startDate, endDate);
  // The dates come prefilled, so they alone do not count as input.
  const isDirty =
    !isSaved &&
    !isExisting &&
    Boolean(dealNumber.trim() || name.trim() || tariffs.length || files.length || isRentBunker);
  React.useEffect(() => onDirtyChange(isDirty), [isDirty, onDirtyChange]);

  const { currentData: contractList, isFetching: loadingContracts } = useListContractsQuery(
    {
      serviceProviderIds: providerId ? [BigInt(providerId)] : [],
      targetOrganizationIds: organizationId != null ? [organizationId] : [],
      // Active only: the container-group modal offers only these, so an archived pick would dead-end.
      status: ContractStatus.CONTRACT_STATUS_ACTIVE,
    },
    { skip: !providerId || organizationId == null || !isExisting },
  );
  const existingContracts = contractList?.contracts ?? [];

  const selectedExisting = React.useMemo(
    () => existingContracts.find((contract) => String(contract.id) === selectedExistingId),
    [existingContracts, selectedExistingId],
  );

  const contractOptions = React.useMemo(
    () =>
      existingContracts.map((contract) => ({
        id: String(contract.id),
        name:
          [contract.dealNumber && `№${contract.dealNumber}`, contract.name]
            .filter(Boolean)
            .join(" · ") || t(Namespace.Customers, "onboarding.contract.untitled"),
      })),
    [existingContracts, t],
  );

  React.useEffect(() => {
    if (!selectedExisting) return;
    setIsEditingExisting(false);
    const values = contractToFormValues(selectedExisting);
    setDealNumber(values.dealNumber);
    setName(values.name);
    setStartDate(values.startDate);
    setEndDate(values.endDate);
    setIsRentBunker(values.isRentBunker);
    setRentCost(values.rentCost);
    setTariffs(values.tariffs);
  }, [selectedExisting]);

  /** Read-only while an existing contract is being shown rather than edited. */
  const readOnly = isExisting && !isEditingExisting;

  const { selectFile } = useContractFilePicker((file) => {
    setFiles((prev) => [...prev, file]);
    if (!name) setName(file.name.split(".")[0]);
  });

  const { byKey: jobTypeOptionByKey } = useJobTypeOptions(t);

  const toggleJobType = (tariffKey: string) =>
    setTariffs((prev) => toggleJobTypeTariff(prev, tariffKey));

  const updateAmount = (type: string, amount: string) =>
    setTariffs((prev) => setJobTypeTariffAmount(prev, type, amount));

  /** Whole tenge, as the RPC's int64 expects; the input is already integer-only. */
  const rentCostValue = () => (isRentBunker ? toAmount(rentCost === "" ? 0 : rentCost) : undefined);

  /** Uploads the pending files; they stay pending on failure so "Обновить" retries them. */
  const uploadFiles = async (id: bigint) => {
    if (files.length === 0) return true;
    try {
      const protoFiles = await Promise.all(files.map(browserFileToProtoFile));
      await uploadContractFiles({ contractId: id, files: protoFiles }).unwrap();
      setFiles([]);
      return true;
    } catch (error) {
      console.error("Failed to upload contract files", error);
      toast({
        variant: "destructive",
        title: t(Namespace.Customers, "onboarding.contract.uploadFailed"),
      });
      return false;
    }
  };

  const handleCreate = async () => {
    if (!providerId || organizationId == null || isDateRangeInvalid) return;
    try {
      const created = await createContract({
        dealNumber: dealNumber.trim(),
        name: name.trim(),
        type: ConnectProto.ContractType.WASTE_OPERATOR,
        serviceProviderId: BigInt(providerId),
        targetOrganizationId: organizationId,
        startAt: toTimestamp(startDate ?? new Date()),
        endAt: toTimestamp(endDate ?? new Date(Date.now() + ONE_YEAR_MS)),
        tariffRateByJobType: buildTariffRate(tariffs),
        rentCost: rentCostValue(),
      }).unwrap();

      // The id is reported before the files go up, so a failed upload is retried
      // against this contract instead of creating a second one.
      const newId = BigInt(created.id);
      setCreatedId(newId);
      onCreated(newId);
      await uploadFiles(newId);
    } catch (error) {
      console.error("Failed to create contract", error);
      toast({
        variant: "destructive",
        title: t(Namespace.Customers, "onboarding.contract.createFailed"),
      });
    }
  };

  const handleUpdate = async (targetId?: bigint) => {
    const id = targetId ?? contractId;
    if (id == null || isDateRangeInvalid) return;
    // Without a target the update is the "new" tab's, and that tab may only touch its own contract.
    if (targetId == null && !createdHere) return;
    try {
      await updateContract({
        id,
        // Org ids go out on update too: the proto marks them "to be required".
        serviceProviderId: providerId ? BigInt(providerId) : undefined,
        targetOrganizationId: organizationId,
        dealNumber: dealNumber.trim(),
        name: name.trim(),
        startAt: startDate ? toTimestamp(startDate) : undefined,
        endAt: endDate ? toTimestamp(endDate) : undefined,
        tariffRateByJobType: buildTariffRate(tariffs),
        rentCost: rentCostValue(),
      }).unwrap();
      if (await uploadFiles(id)) {
        toast({ title: t(Namespace.Customers, "onboarding.contract.updated") });
      }
    } catch (error) {
      console.error("Failed to update contract", error);
      toast({
        variant: "destructive",
        title: t(Namespace.Customers, "onboarding.contract.updateFailed"),
      });
    }
  };

  const primary = isExisting
    ? {
        label: t(Namespace.Customers, "onboarding.contract.pick"),
        onClick: () => selectedExisting && onPickedExisting(BigInt(selectedExisting.id)),
        disabled: !selectedExisting || isBusy,
      }
    : {
        label: t(Namespace.Customers, "onboarding.contract.create"),
        onClick: handleCreate,
        disabled:
          isSaved || isBusy || !dealNumber.trim() || organizationId == null || isDateRangeInvalid,
      };

  // On the existing tab the secondary first unlocks the form, then saves it.
  const secondary = isExisting
    ? selectedExisting
      ? {
          label: isEditingExisting
            ? t(Namespace.Customers, "onboarding.contract.update")
            : t(Namespace.Customers, "onboarding.contract.edit"),
          onClick: () =>
            isEditingExisting
              ? handleUpdate(BigInt(selectedExisting.id))
              : setIsEditingExisting(true),
          disabled: isBusy || (isEditingExisting && (!dealNumber.trim() || isDateRangeInvalid)),
        }
      : undefined
    : // A contract picked on the other tab is not this tab's draft: no Update over it.
      isSaved && createdHere
      ? {
          label: t(Namespace.Customers, "onboarding.contract.update"),
          onClick: () => handleUpdate(),
          disabled: isBusy || !dealNumber.trim() || isDateRangeInvalid,
        }
      : undefined;

  return (
    <StepShell primary={primary} secondary={secondary}>
      <div className="flex w-full flex-col gap-8">
        <ModeTabs
          value={mode}
          onChange={setMode}
          options={[
            { value: "new", label: t(Namespace.Customers, "onboarding.contract.tabNew") },
            { value: "existing", label: t(Namespace.Customers, "onboarding.contract.tabExisting") },
          ]}
        />

        {/* The customer is fixed by step 1: a disabled box, not a picker. */}
        {isExisting && (
          <Section title={t(Namespace.Customers, "onboarding.step.contract")}>
            <div className="flex w-full items-center gap-12">
              <div className={cn(DISABLED_BOX_CLASS, "w-[300px] shrink-0")} title={customerName}>
                <span className="truncate">
                  {customerName || t(Namespace.Customers, "onboarding.step.customer")}
                </span>
              </div>
              <OnboardingField
                label={t(Namespace.Customers, "onboarding.contract.pickLabel")}
                htmlFor="onboarding-existing-contract"
                labelWidth={120}
                className="flex-1"
              >
                <SearchableSelect
                  id="onboarding-existing-contract"
                  value={selectedExistingId}
                  onChange={setSelectedExistingId}
                  options={contractOptions}
                  loading={loadingContracts}
                  placeholder={t(Namespace.Customers, "onboarding.contract.pickPlaceholder")}
                  searchPlaceholder={t(Namespace.Customers, "searchPlaceholder")}
                />
              </OnboardingField>
            </div>
          </Section>
        )}

        <Section title={t(Namespace.Customers, "onboarding.contract.numberSection")}>
          <div className="flex w-full items-center gap-4">
            {!isExisting && (
              <div className={cn(DISABLED_BOX_CLASS, "w-[300px] shrink-0")} title={customerName}>
                <span className="truncate">
                  {customerName || t(Namespace.Customers, "onboarding.step.customer")}
                </span>
              </div>
            )}
            <div
              className={cn(
                "flex h-8 shrink-0 items-center gap-1 rounded-lg border px-2",
                readOnly ? "border-slate-300 bg-slate-200" : "border-slate-200 bg-white",
              )}
            >
              <span
                className={cn(
                  "text-xs font-medium",
                  readOnly ? "text-icon-secondary" : "text-text-body",
                )}
              >
                №
              </span>
              <input
                id="onboarding-contract-number"
                value={dealNumber}
                disabled={readOnly}
                onChange={(e) => setDealNumber(e.target.value)}
                placeholder={t(Namespace.Customers, "onboarding.contract.numberPlaceholder")}
                className="h-full w-32 border-none bg-transparent text-sm outline-none disabled:text-slate-400"
              />
            </div>
            <Input
              id="onboarding-contract-name"
              value={name}
              disabled={readOnly}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
              placeholder={t(Namespace.Customers, "onboarding.contract.namePlaceholder")}
              className={cn("flex-1", DISABLED_INPUT_CLASS)}
            />
          </div>
        </Section>

        <Section title={t(Namespace.Customers, "onboarding.contract.termsSection")}>
          <div className="flex items-center gap-16">
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium tracking-[-0.2px] text-text-body">
                {t(Namespace.Customers, "onboarding.contract.startDate")}
              </span>
              <DatePicker
                value={startDate}
                disabled={readOnly}
                onChange={setStartDate}
                maxDate={endDate}
                className="flex max-h-8 w-32 items-center gap-1 rounded-lg bg-white !px-2 !py-2"
                placeholder={t(Namespace.Customers, "modal.addContract.selectDate")}
              />
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium tracking-[-0.2px] text-text-body">
                {t(Namespace.Customers, "onboarding.contract.endDate")}
              </span>
              <DatePicker
                value={endDate}
                disabled={readOnly}
                onChange={setEndDate}
                minDate={startDate}
                className="flex max-h-8 w-32 items-center gap-1 rounded-lg bg-white !px-2 !py-2"
                placeholder={t(Namespace.Customers, "modal.addContract.selectDate")}
              />
            </div>
          </div>
          {isDateRangeInvalid && (
            <p className="text-xs text-text-error">{t(Namespace.Validation, "dateRangeInvalid")}</p>
          )}
        </Section>

        <Section
          title={t(Namespace.Customers, "onboarding.contract.tariffsSection")}
          action={
            <JobTypePicker
              tariffs={tariffs}
              onToggle={toggleJobType}
              inputId="onboarding-contract-job-type-search"
              disabled={readOnly}
            />
          }
        >
          {tariffs.map((tariff) => {
            const option = jobTypeOptionByKey.get(tariff.type);
            return (
              <OnboardingField
                key={tariff.type}
                required
                label={
                  option?.label ?? t(Namespace.Enum, tariff.type as TranslationKey<Namespace.Enum>)
                }
              >
                <div className="flex items-center gap-2">
                  <div
                    className={cn(
                      "flex h-8 max-w-[260px] flex-1 items-center rounded-lg border px-2",
                      readOnly ? "border-slate-300 bg-slate-200" : "border-slate-200 bg-white",
                    )}
                  >
                    <input
                      type="number"
                      step="1"
                      min="0"
                      value={tariff.amount}
                      disabled={readOnly}
                      onKeyDown={(e) => {
                        if (isRejectedAmountKey(e.key)) e.preventDefault();
                      }}
                      onChange={(e) => updateAmount(tariff.type, e.target.value)}
                      placeholder="0"
                      className="h-full min-w-0 flex-1 border-none bg-transparent text-sm text-text-body outline-none disabled:text-slate-400"
                    />
                    <span className="ml-2 whitespace-nowrap text-sm text-icon-secondary">
                      {t(Namespace.Customers, "onboarding.contract.tariffUnit")}
                    </span>
                  </div>
                  <Button
                    type="button"
                    size="icon"
                    variant="opacityRedWithoutOutline"
                    className="!h-8 !w-10"
                    disabled={readOnly}
                    onClick={() => setTariffs((prev) => prev.filter((x) => x.type !== tariff.type))}
                  >
                    <IcBin className="h-4 w-4" />
                  </Button>
                </div>
              </OnboardingField>
            );
          })}
        </Section>

        <Section title={t(Namespace.Customers, "onboarding.contract.rentSection")}>
          <div className="flex h-8 items-center gap-16">
            <label className="flex cursor-pointer items-center gap-2">
              <Checkbox
                id="onboarding-contract-rent"
                checked={isRentBunker}
                disabled={readOnly}
                onCheckedChange={(checked) => {
                  const next = checked === true;
                  setIsRentBunker(next);
                  if (!next) setRentCost(0);
                }}
              />
              <span className="text-sm font-medium tracking-[-0.2px] text-text-body">
                {t(Namespace.Customers, "onboarding.contract.bunkerRent")}
              </span>
            </label>

            <div
              className={cn(
                "flex h-8 w-72 items-center rounded-lg border px-2",
                isRentBunker && !readOnly
                  ? "border-slate-200 bg-white"
                  : "border-slate-300 !bg-slate-200",
              )}
            >
              <input
                type="number"
                min="0"
                step="1"
                value={rentCost}
                disabled={readOnly || !isRentBunker}
                onKeyDown={(e) => {
                  if (isRejectedAmountKey(e.key)) e.preventDefault();
                }}
                // Clamped, not just hinted by min: a pasted or typed "-1" must not reach the RPC.
                onChange={(e) => setRentCost(toNonNegativeInteger(e.target.value))}
                placeholder="0"
                className="h-full min-w-0 flex-1 border-none bg-transparent text-sm text-text-body outline-none disabled:text-slate-400"
              />
              <span
                className={cn(
                  "ml-2 whitespace-nowrap text-sm",
                  isRentBunker && !readOnly ? "text-icon-secondary" : "text-slate-400",
                )}
              >
                {t(Namespace.Customers, "onboarding.contract.rentUnit")}
              </span>
            </div>
          </div>
        </Section>

        <Section title={t(Namespace.Customers, "onboarding.contract.fileSection")}>
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium tracking-[-0.2px] text-text-body">
              {files.length > 0
                ? files.map((file) => file.name).join(", ")
                : t(Namespace.Customers, "onboarding.contract.fileNotUploaded")}
            </span>
            {/* A picked contract's files go up only from an update, so the picker waits for "Редактировать". */}
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={readOnly}
              onClick={selectFile}
              className="bg-blue-100 px-3 py-2 text-sm font-medium text-blue-600 hover:bg-blue-200 disabled:text-slate-400"
            >
              {t(Namespace.Customers, "onboarding.contract.uploadFile")}
            </Button>
          </div>
        </Section>
      </div>
    </StepShell>
  );
};
